import { useMemo } from "react";

import { Chess } from "chess.js";

import { replayUciMove } from "@/shared/ui/chessMove";

import type { LivestreamSnapshot } from "./types";

type MoveEntry = {
  ply: number;
  san: string;
  uci: string;
  side: "white" | "black";
};

type MoveRow = {
  moveNumber: number;
  white: MoveEntry | null;
  black: MoveEntry | null;
};

type LivestreamMoveHistoryProps = {
  snapshot: LivestreamSnapshot;
  activePly: number;
  onOpenPly: (ply: number) => void;
};

function buildMoveRows(snapshot: LivestreamSnapshot) {
  const board = snapshot.initial_fen ? new Chess(snapshot.initial_fen) : new Chess();
  const entries: MoveEntry[] = [];

  for (const [index, uci] of snapshot.visible_moves.entries()) {
    const move = replayUciMove(board, uci);
    entries.push({
      ply: index + 1,
      san: move?.san ?? uci,
      uci,
      side: move?.color === "b" ? "black" : "white",
    });
  }

  const rows: MoveRow[] = [];
  for (const entry of entries) {
    if (entry.side === "white" || rows.length === 0 || rows.at(-1)?.black) {
      rows.push({
        moveNumber: rows.length + 1,
        white: entry.side === "white" ? entry : null,
        black: entry.side === "black" ? entry : null,
      });
      continue;
    }
    rows[rows.length - 1].black = entry;
  }

  return rows;
}

export function LivestreamMoveHistory({
  snapshot,
  activePly,
  onOpenPly,
}: LivestreamMoveHistoryProps) {
  const rows = useMemo(() => buildMoveRows(snapshot), [snapshot]);

  if (rows.length === 0) {
    return <div className="move-log__empty">Waiting for first move.</div>;
  }

  return (
    <div className="move-log" aria-label="Livestream move history">
      {rows.map((row) => (
        <div
          key={row.moveNumber}
          className={
            row.white?.ply === activePly || row.black?.ply === activePly
              ? "move-log__row move-log__row--active"
              : "move-log__row"
          }
        >
          <span className="move-log__index">{row.moveNumber}.</span>
          <span className="move-log__move">
            {row.white ? (
              <button
                type="button"
                className={
                  activePly === row.white.ply
                    ? "move-log__move-button move-log__move-button--active"
                    : "move-log__move-button"
                }
                onClick={() => onOpenPly(row.white?.ply ?? 0)}
              >
                {row.white.san}
              </button>
            ) : (
              <span className="move-log__move-placeholder" />
            )}
          </span>
          <span className="move-log__move">
            {row.black ? (
              <button
                type="button"
                className={
                  activePly === row.black.ply
                    ? "move-log__move-button move-log__move-button--active"
                    : "move-log__move-button"
                }
                onClick={() => onOpenPly(row.black?.ply ?? 0)}
              >
                {row.black.san}
              </button>
            ) : (
              <span className="move-log__move-placeholder" />
            )}
          </span>
        </div>
      ))}
    </div>
  );
}
